import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, Star, Shield, Loader2, Ticket } from 'lucide-react';
import { Button } from './ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { useModalStore } from '@/lib/stores/modal-store';
import { AuthModal } from './auth/AuthModal';
import { logger } from '@/lib/utils/logger';

// Memoized AuthModal to prevent recreation when user state changes
const MemoizedAuthModal = React.memo(function MemoizedAuthModal() {
  const { authModal, closeAuthModal } = useModalStore();
  
  return (
    <AuthModal 
      isOpen={authModal.isOpen}
      onClose={closeAuthModal}
      defaultTab={authModal.tab}
    />
  );
});

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const navigate = useNavigate();
  const { user, profile, signOut, isLoading } = useUnifiedAuth();
  const { openAuthModal } = useModalStore();

  // Handle scroll effect - memoized so it doesn't rebuild on every render
  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    // Add scroll listener just once
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Memoized handlers to prevent excessive re-renders
  const handleAuth = useCallback((type: 'sign-in' | 'sign-up') => {
    openAuthModal(type);
  }, [openAuthModal]);

  const handleProfile = useCallback(() => {
    navigate('/profile');
    setIsOpen(false);
  }, [navigate]);

  const handleSignOut = useCallback(async () => {
    if (isSigningOut) return; // Prevent multiple clicks
    
    try {
      setIsSigningOut(true);
      logger.info('Sign out button clicked');
      await signOut();
      logger.info('Sign out successful');
      
      // Force navigation on mobile devices
      if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        window.location.href = '/';
      } else {
        navigate('/');
      }
      
      setIsOpen(false);
    } catch (error) {
      logger.error('Sign out error', { error });
    } finally {
      setIsSigningOut(false);
    }
  }, [signOut, navigate, isSigningOut]);

  // Simple mobile menu toggle
  const handleMenuToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  // Determine if user has creator/admin privileges (with fallback during loading)
  const isCreatorOrAdmin = profile?.role === 'creator' || profile?.role === 'admin';
  // Use session user role first, fall back to profile role
  const userRole = user?.user_metadata?.role || profile?.role;
  const isAdmin = userRole === 'admin';
  
  // Show creator/admin buttons immediately if user exists, even if profile is loading
  const showCreatorButton = user && (isCreatorOrAdmin || isLoading);
  const showAdminButton = user && (isAdmin || (isLoading && user.email?.includes('@letsrendezvous.co')));

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-earth-900/95 backdrop-blur-sm border-b border-earth-800' : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="font-display text-2xl tracking-wider text-sand-100">
              rendezvous
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <NavLink href="/experiences">Experiences</NavLink>
              <NavLink href="/creators">Creators</NavLink>
              <NavLink href="/about">About</NavLink>
              {user ? (
                <div className="flex items-center space-x-4">
                  {showAdminButton && (
                    <Button variant="outline" size="sm" onClick={() => navigate('/admin')}>
                      <Shield className="h-4 w-4 mr-2" />
                      Admin
                    </Button>
                  )}
                  {showCreatorButton && (
                    <Button variant="outline" size="sm" onClick={() => navigate('/creator/dashboard')}>
                      <Star className="h-4 w-4 mr-2" />
                      Creator Dashboard
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => navigate('/bookings')}>
                    <Ticket className="h-4 w-4 mr-2" />
                    My Bookings
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleProfile}>
                    <User className="h-4 w-4 mr-2" />
                    Profile
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleSignOut}
                    onTouchEnd={handleSignOut}
                    disabled={isSigningOut}
                    className="touch-manipulation"
                  >
                    {isSigningOut ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Sign Out'
                    )}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <Button variant="outline" size="sm" onClick={() => handleAuth('sign-in')}>
                    Login
                  </Button>
                  <Button size="sm" onClick={() => handleAuth('sign-up')}>
                    Join Now
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-sand-100 touch-manipulation"
              onClick={handleMenuToggle}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation - Optimized for immediate response */}
        <AnimatePresence>
          {isOpen && (
            <motion.div 
              className="md:hidden bg-earth-900/95 backdrop-blur-sm border-b border-earth-800"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="container mx-auto px-4 py-8 space-y-6">
                <MobileNavLink href="/experiences" onClick={() => setIsOpen(false)}>
                  Experiences
                </MobileNavLink>
                <MobileNavLink href="/creators" onClick={() => setIsOpen(false)}>
                  Creators
                </MobileNavLink>
                <MobileNavLink href="/about" onClick={() => setIsOpen(false)}>
                  About
                </MobileNavLink>
                {user ? (
                  <>
                    {showAdminButton && (
                      <Button 
                        variant="outline" 
                        className="w-full touch-manipulation"
                        onClick={() => {
                          navigate('/admin');
                          setIsOpen(false);
                        }}
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        Admin
                        {isLoading && <Loader2 className="h-3 w-3 ml-2 animate-spin" />}
                      </Button>
                    )}
                    {showCreatorButton && (
                      <Button 
                        variant="outline" 
                        className="w-full touch-manipulation"
                        onClick={() => {
                          navigate('/creator/dashboard');
                          setIsOpen(false);
                        }}
                      >
                        <Star className="h-4 w-4 mr-2" />
                        Creator Dashboard
                        {isLoading && <Loader2 className="h-3 w-3 ml-2 animate-spin" />}
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      className="w-full touch-manipulation"
                      onClick={() => {
                        navigate('/bookings');
                        setIsOpen(false);
                      }}
                    >
                      <Ticket className="h-4 w-4 mr-2" />
                      My Bookings
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full touch-manipulation"
                      onClick={handleProfile}
                    >
                      <User className="h-4 w-4 mr-2" />
                      Profile
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full touch-manipulation"
                      onClick={handleSignOut}
                      onTouchEnd={handleSignOut}
                      disabled={isSigningOut}
                    >
                      {isSigningOut ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      className="w-full touch-manipulation"
                      onClick={() => {
                        setIsOpen(false);
                        handleAuth('sign-in');
                      }}
                    >
                      Login
                    </Button>
                    <Button 
                      className="w-full touch-manipulation"
                      onClick={() => {
                        setIsOpen(false);
                        handleAuth('sign-up');
                      }}
                    >
                      Create Account
                    </Button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <MemoizedAuthModal />
    </>
  );
}

// Memoized link components
const NavLink = React.memo(({ href, children }: { href: string; children: React.ReactNode }) => {
  return (
    <Link 
      to={href}
      className="text-sm uppercase tracking-wider text-sand-100 hover:text-sand-300 transition-colors"
    >
      {children}
    </Link>
  );
});

const MobileNavLink = React.memo(
  ({ href, children, onClick }: { href: string; children: React.ReactNode; onClick?: () => void }) => {
    return (
      <Link 
        to={href}
        className="block text-lg font-display text-sand-100 hover:text-sand-300 transition-colors touch-manipulation"
        onClick={onClick}
        style={{ 
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        {children}
      </Link>
    );
  }
);